import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap } from "@vixio/data";
import { registerAuditLabels, type StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo talks (ver modules/talks/README.md). */

export type TalkStatus = "requested" | "qualifying" | "proposed" | "confirmed" | "delivered" | "declined" | "canceled";

export const TALK_STATUS: StatusMap = {
  requested: { label: "pedido novo", tone: "info" },
  qualifying: { label: "em qualificação", tone: "accent" },
  proposed: { label: "proposta enviada", tone: "warn" },
  confirmed: { label: "confirmada", tone: "ok" },
  delivered: { label: "entregue", tone: "ok" },
  declined: { label: "recusada", tone: "neutral" },
  canceled: { label: "cancelada", tone: "neutral" },
};
export const PROPOSAL_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  sent: { label: "enviada", tone: "info" },
  accepted: { label: "aceita", tone: "ok" },
  rejected: { label: "recusada", tone: "crit" },
  superseded: { label: "substituída", tone: "neutral" },
};
export const MODALITY_LABEL: Record<string, string> = { presencial: "Presencial", online: "Online", hibrido: "Híbrida" };

/** Perguntas de qualificação ao organizador (PM3 §14.1). */
export const QUALIFICATION: Array<{ key: "publico" | "duracao" | "espaco_dados" | "cache" | "gravacao" | "observacoes"; label: string }> = [
  { key: "publico", label: "Quem é o público (cargos)?" },
  { key: "duracao", label: "Qual a duração?" },
  { key: "espaco_dados", label: "Há espaço para dados e caso real?" },
  { key: "cache", label: "Há cachê ou é convite institucional?" },
  { key: "gravacao", label: "Haverá gravação?" },
  { key: "observacoes", label: "Observações" },
];

export const SCOPE_FIELDS: Array<{ key: "objetivo" | "publico" | "tema" | "formato" | "preparacao" | "material" | "logistica"; label: string }> = [
  { key: "objetivo", label: "Objetivo" },
  { key: "publico", label: "Público" },
  { key: "tema", label: "Tema" },
  { key: "formato", label: "Formato" },
  { key: "preparacao", label: "Preparação" },
  { key: "material", label: "Material" },
  { key: "logistica", label: "Logística" },
];

export interface TalkView {
  id: string;
  /** Marcada para a agenda do site (talks 0003). */
  public_listing?: boolean;
  org_id: string;
  code: string;
  title: string;
  status: TalkStatus;
  origin: "site" | "team";
  topic_id: string | null;
  topic_slug: string | null;
  topic_title: string | null;
  speaker_id: string | null;
  speaker_name: string | null;
  person_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  organizer_name: string | null;
  event_name: string | null;
  audience_roles: string | null;
  audience_size: number | null;
  event_date: string | null;
  starts_at: string | null;
  duration_min: number;
  modality: string;
  city: string | null;
  location: string | null;
  online_url: string | null;
  request_message: string | null;
  request_has_fee: boolean | null;
  request_recording: boolean | null;
  qualification: Record<string, string>;
  decline_reason: string | null;
  decline_note: string | null;
  fee_cents: number | null;
  expenses_cents: number | null;
  accepted_proposal_id: string | null;
  issuer_id: string | null;
  recording_authorized: boolean | null;
  image_use_authorized: boolean | null;
  canceled_at: string | null;
  canceled_by: string | null;
  cancel_reason: string | null;
  cancellation_fee_cents: number | null;
  confirmed_at: string | null;
  delivered_at: string | null;
  attendees_count: number | null;
  eval_closes_at: string | null;
  notes: string | null;
  img_url: string;
  participants_count: number;
  evaluations_count: number;
  created_at: string;
}

export interface Proposal {
  id: string;
  talk_id: string;
  version: number;
  fee_cents: number;
  expenses_cents: number;
  free_reason: string | null;
  scope: Record<string, string>;
  payment_terms: string | null;
  cancellation_notice_days: number;
  cancellation_fee_pct: number;
  recording_terms: string | null;
  image_use_terms: string | null;
  valid_until: string | null;
  status: "draft" | "sent" | "accepted" | "rejected" | "superseded";
  sent_at: string | null;
  decided_at: string | null;
  decided_by_name: string | null;
  decision_note: string | null;
  created_at: string;
}

export interface Authorization {
  id: string;
  kind: "gravacao" | "imagem";
  granted: boolean;
  granted_by_name: string;
  evidence: string | null;
  recorded_at: string;
}

export interface Speaker {
  id: string;
  slug: string;
  name: string;
  headline: string | null;
  bio: string | null;
  main_topic: string | null;
  audience: string | null;
  format: string | null;
  deliverable: string | null;
  proof: string | null;
  sample_url: string | null;
  contact: string | null;
  active: boolean;
}

export interface Topic {
  id: string;
  slug: string;
  title: string;
  audience: string | null;
  takeaway: string | null;
  summary: string | null;
  duration_min: number;
  outline: Array<{ from: number; to: number; label: string }>;
  base_fee_cents: number;
  speaker_id: string | null;
  active: boolean;
}

export interface DeclineReason {
  code: string;
  label: string;
}

export function useDeclineReasons() {
  return useQuery({ queryKey: ["talks", "decline_reasons"], staleTime: Infinity, queryFn: () => rpc("talks", "decline_reasons", {}) });
}
export function useTopics(orgId: string) {
  return useQuery({ queryKey: ["talks", "topics", orgId], queryFn: () => unwrap<Topic[]>(from("talks", "topics").select("*").eq("org_id", orgId).order("title")) });
}
export function useSpeakers(orgId: string) {
  return useQuery({ queryKey: ["talks", "speakers", orgId], queryFn: () => unwrap<Speaker[]>(from("talks", "speakers").select("*").eq("org_id", orgId).order("name")) });
}

declare module "@vixio/data" {
  interface RpcRegistry {
    "talks.decline_reasons": { args: Record<string, never>; returns: DeclineReason[] };
    "talks.upsert_speaker": {
      args: {
        p_org: string; p_slug: string; p_name: string; p_headline?: string | null; p_bio?: string | null; p_main_topic?: string | null;
        p_audience?: string | null; p_format?: string | null; p_deliverable?: string | null; p_proof?: string | null;
        p_sample_url?: string | null; p_contact?: string | null; p_active?: boolean;
      };
      returns: string;
    };
    "talks.upsert_topic": {
      args: {
        p_org: string; p_slug: string; p_title: string; p_audience?: string | null; p_takeaway?: string | null; p_summary?: string | null;
        p_duration_min?: number; p_outline?: Array<{ from: number; to: number; label: string }>; p_base_fee_cents?: number;
        p_speaker?: string | null; p_active?: boolean;
      };
      returns: string;
    };
    "talks.create_talk": {
      args: {
        p_org: string; p_person: string | null; p_organizer_name: string; p_title?: string | null; p_topic?: string | null;
        p_event_name?: string | null; p_event_date?: string | null; p_duration_min?: number | null; p_modality?: string;
        p_city?: string | null; p_audience_roles?: string | null; p_audience_size?: number | null; p_notes?: string | null;
      };
      returns: string;
    };
    "talks.update_talk": {
      args: {
        p_talk: string; p_title: string; p_event_name?: string | null; p_event_date?: string | null; p_starts_at?: string | null;
        p_duration_min?: number; p_modality?: string; p_city?: string | null; p_location?: string | null; p_online_url?: string | null;
        p_audience_roles?: string | null; p_audience_size?: number | null; p_topic?: string | null; p_issuer?: string | null;
        p_organizer_name?: string | null; p_notes?: string | null;
      };
      returns: null;
    };
    "talks.qualify": { args: { p_talk: string; p_answers: Record<string, string> }; returns: null };
    "talks.decline": { args: { p_talk: string; p_reason: string; p_note?: string | null }; returns: null };
    "talks.create_proposal": {
      args: {
        p_talk: string; p_fee_cents: number; p_expenses_cents?: number; p_scope?: Record<string, string>; p_payment_terms?: string | null;
        p_cancellation_notice_days?: number; p_cancellation_fee_pct?: number; p_recording_terms?: string | null;
        p_image_use_terms?: string | null; p_valid_until?: string | null; p_free_reason?: string | null;
      };
      returns: string;
    };
    "talks.send_proposal": { args: { p_proposal: string }; returns: null };
    "talks.accept_proposal": { args: { p_proposal: string; p_accepted_by_name: string; p_note?: string | null }; returns: null };
    "talks.reject_proposal": { args: { p_proposal: string; p_note: string }; returns: null };
    "talks.record_authorization": {
      args: { p_talk: string; p_kind: "gravacao" | "imagem"; p_granted: boolean; p_granted_by_name: string; p_evidence?: string | null };
      returns: string;
    };
    "talks.set_public_listing": { args: { p_talk: string; p_public: boolean }; returns: null };
    "talks.cancel": { args: { p_talk: string; p_reason: string; p_canceled_by: "organizador" | "vixio" }; returns: { cancellation_fee_cents: number; days_before: number | null } };
    "talks.add_participant": { args: { p_talk: string; p_name: string; p_email?: string | null; p_phone?: string | null }; returns: string };
    "talks.add_participants": { args: { p_talk: string; p_people: string[] }; returns: number };
    "talks.deliver": {
      args: { p_talk: string; p_attendees_count?: number | null; p_hours?: number | null; p_certificates?: boolean };
      returns: { evaluation_token: string; participants: number; evaluation_closes_at: string };
    };
    "talks.rotate_evaluation_link": { args: { p_talk: string }; returns: string };
    "talks.install": { args: { p_org: string }; returns: string };
    "talks.evaluation_form": { args: { p_token: string }; returns: EvaluationForm };
    "talks.submit_evaluation": { args: { p_token: string; p_answers: Record<string, unknown>; p_consents?: Record<string, boolean> }; returns: { ok: boolean } };
  }
}

export interface EvaluationItem {
  id: string;
  type: "nps" | "rating" | "long_text" | "short_text" | "likert" | "single_choice";
  text: string;
  required?: boolean;
  max_length?: number;
  options?: Array<string | { value: string; label: string }>;
}
export interface EvaluationForm {
  title: string;
  intro?: string;
  items: EvaluationItem[];
  talk: { title: string; event_name?: string; date?: string; code: string };
}

registerAuditLabels({
  "talks.speaker_upserted": "Ficha de palestrante salva",
  "talk.public_listing": "Agenda do site da palestra alterada",
  "talks.topic_upserted": "Palestra do catálogo salva",
  "talks.talk_requested": "Pedido de palestra registrado",
  "talks.talk_updated": "Dados da palestra alterados",
  "talks.talk_qualified": "Qualificação registrada",
  "talks.talk_declined": "Pedido recusado",
  "talks.proposal_created": "Proposta criada",
  "talks.proposal_sent": "Proposta enviada",
  "talks.proposal_rejected": "Proposta recusada",
  "talks.talk_confirmed": "Palestra confirmada",
  "talks.authorization_recorded": "Autorização registrada",
  "talks.talk_canceled": "Palestra cancelada",
  "talks.participants_added": "Participantes incluídos",
  "talks.talk_delivered": "Palestra entregue",
  "talks.evaluation_link_rotated": "Novo link de avaliação",
});
